import { Flex } from "@chakra-ui/react"
import { searchNavigationParams } from "../../../../common/constants/constants"
import { COLOUR, searchResultStatus } from "../../../../common/constants/search-constants"
import { useBrandedTranslation } from "../../../../common/hooks/useBrandedTranslation"
import { useQueryParam } from "../../../../common/lib/QueryParamContext"
import { Tab } from "../../../../common/types/global"
import { FhResponseData } from "../../../../common/types/search"
import { getNavigationSearchTerm } from "../../../../common/utils/search-utils"
import CategoryBreadcrumbs from "../CategoryDrillDownWrapper/CategoryBreadcrumbs/CategoryBreadcrumbs"
import CategoryDrillDownWrapper from "../CategoryDrillDownWrapper/CategoryDrillDownWrapper"
import CorrectionHint from "../CorrectionHint/CorrectionHint"
import FiltersWrapper from "../FiltersWrapper/FiltersWrapper"
import NoResultsFiltersHint from "../NoResultsFiltersHint/NoResultsFiltersHint"
import NoResultsView from "../NoResultsView/NoResultstView"
import PartialResultsHint from "../PartialResultsView/PartialResultsHint/PartialResultsHint"
import RemoveFilters from "../RemoveFilters/RemoveFilters"
import ResultsCountInfo from "../ResultsCountInfo/ResultsCountInfo"
import ResultsWrapper from "../ResultsWrapper/ResultsWrapper"
import SearchBreadcrumbs from "../SearchBreadcrumbs/SearchBreadcrumbs"
import SearchTabs from "../SearchTabs/SearchTabs"
import PartialResultsContainer from "../containers/PartialResultsContainer"

type Props = {
  transformedMeta: FhResponseData
  searchResult: FhResponseData
  selectedTab: Tab
  handleSetSelectedTab: (tab: Tab, urlParams?: string) => void
  pageNumber: number
  setPageNumber: (pageNumber: number | ((prevPageNumber: number) => number)) => void
  isLoading: boolean
  host: string
}

const SearchResultsView = ({
  transformedMeta,
  searchResult,
  selectedTab,
  handleSetSelectedTab,
  pageNumber,
  setPageNumber,
  isLoading,
  host,
}: Props) => {
  const { queryParam } = useQueryParam()
  const { t } = useBrandedTranslation()
  const currentTabCount =
    transformedMeta?.typeSelector?.filtersection?.find((f) => f.value.value === selectedTab)?.nr ?? 0

  //If we don't have a searchTerm, try to get it from the navigation referrer
  if (transformedMeta.searchTerm === null && queryParam) {
    transformedMeta.searchTerm = getNavigationSearchTerm(transformedMeta, queryParam, t("nav-newReleases"))
  }

  const isPartialResults = transformedMeta.searchPass === searchResultStatus.HAS_PARTIALS

  /* We don't want to show the color filter if we have colour in implicitRanges */
  if (COLOUR in (searchResult?.implicitRanges ?? {})) {
    searchResult.allSearchFilters = searchResult?.allSearchFilters?.filter((filter) => filter?.on !== COLOUR)
  }

  return (
    <>
      {queryParam.name !== searchNavigationParams.SERIES && (
        <SearchBreadcrumbs host={host} breadcrumbsData={transformedMeta.crumb} selectedTab={selectedTab} />
      )}

      {/* If we have an error in the meta response or no results in the selection show the "0 results page" */}
      {transformedMeta.showNoResultsView && !transformedMeta.showFiltersNoResultsView ? (
        <NoResultsView searchTerm={transformedMeta.searchTerm} />
      ) : (
        <>
          {/* If we have the original term stored, it has been corrected, then show the correction hint */}
          {transformedMeta.originalSearchTerm && (
            <CorrectionHint originalSearchTerm={transformedMeta.originalSearchTerm} />
          )}
          {/* If we have no results, but there are filter values applied show hint to the user to remove a filter value to continue */}
          {transformedMeta.showFiltersNoResultsView && <NoResultsFiltersHint />}
          {/* If the status of the meta response indicates partial results present then show the partial results hint */}
          {isPartialResults && <PartialResultsHint />}
          {/* The main title of the search page */}
          <ResultsCountInfo
            searchTerm={transformedMeta.searchTerm}
            numberOfItems={transformedMeta.nr_of_items_in_selection}
          />
          {/* If the status of the meta response indicates partial results present then show the partial results page */}
          {isPartialResults ? (
            <PartialResultsContainer
              queryAlternatives={transformedMeta.queryAlternatives}
              searchTerm={transformedMeta.searchTerm}
            />
          ) : (
            <>
              <CategoryBreadcrumbs categories={transformedMeta.categories} breadcrumbsData={transformedMeta.crumb} />
              <Flex
                as="section"
                flexDirection={{ base: "column", sm: "row", md: "column" }}
                gap={{ base: "4", sm: "6", md: "8" }}
                align="flex-end"
                justify="space-between"
              >
                <CategoryDrillDownWrapper
                  breadcrumbsData={transformedMeta.crumb}
                  categories={transformedMeta.categories}
                  searchTerm={transformedMeta.searchTerm}
                />
                <FiltersWrapper
                  globalFilters={transformedMeta.globalFilters}
                  allSearchFilters={searchResult.allSearchFilters}
                  breadcrumbFilters={transformedMeta.breadcrumbFilters}
                  selectedTab={selectedTab}
                  showFiltersNoResultsView={transformedMeta.showFiltersNoResultsView}
                  currentTabCount={currentTabCount}
                  rid={searchResult.rid}
                />
              </Flex>
              {transformedMeta.breadcrumbFilters && (
                <RemoveFilters
                  breadcrumbFilters={transformedMeta.breadcrumbFilters}
                  breadcrumbsAttributeTypes={transformedMeta.breadcrumbsAttributeTypes}
                />
              )}
              {queryParam.name !== searchNavigationParams.NEW &&
                queryParam.name !== searchNavigationParams.CROSS &&
                queryParam.name !== searchNavigationParams.MIX_AND_MATCH &&
                queryParam.name !== searchNavigationParams.SERIES &&
                queryParam.name !== searchNavigationParams.ALL_SPARE_PARTS && (
                  <SearchTabs
                    typeSelector={transformedMeta.typeSelector}
                    selectedTab={selectedTab}
                    handleSetSelectedTab={handleSetSelectedTab}
                  />
                )}
              <ResultsWrapper
                items={searchResult.items}
                selectedTab={selectedTab}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                typeSelector={transformedMeta.typeSelector}
                isLoading={isLoading}
                searchTerm={transformedMeta.searchTerm}
                implicitRanges={searchResult.implicitRanges}
                selectedColors={searchResult.selectedColors}
                rid={searchResult.rid}
              />
            </>
          )}
        </>
      )}
    </>
  )
}

export default SearchResultsView
